import { createContext, useContext, useState } from 'react';
import {
  addUserContactToConsultsBatch,
  getUserSigned,
  IAddUserContactToConsultsBatchRequest,
  IUserData,
} from '../hooks/useUsers';
import { isValidCNPJ, isValidCPF } from '../utils/validateUserDocument';

interface ContactComunixFormDataContextData {
  addSignedUserContactToComunixBatch: (batchCode: number) =>  Promise<void>;

  namePartner: string | null;
  setNamePartner: (name: string) => void;
  namePartnerStatus: 'Error' | '';
  setNamePartnerStatus: React.Dispatch<React.SetStateAction<'' | 'Error'>>;
  namePartnerError: string;
  setNamePartnerError: (error: string) => void;

  cnpj: string | null;
  cpf: string | null;
  setCnpj: (cpfCnpj: string) => void;
  setCpf: (cpfCnpj: string) => void;
  cpfCnpjStatus: 'Error' | '';
  setCpfCnpjStatus: React.Dispatch<React.SetStateAction<'' | 'Error'>>;
  cpfCnpjError: string;
  setCpfCnpjError: (error: string) => void;

  email: string | null;
  setEmail: (email: string) => void;
  emailStatus: 'Error' | '';
  setEmailStatus: React.Dispatch<React.SetStateAction<'' | 'Error'>>;
  emailError: string;
  setEmailError: (error: string) => void;

  phone: string | null;
  setPhone: (phone: string) => void;
  phoneStatus: 'Error' | '';
  setPhoneStatus: React.Dispatch<React.SetStateAction<'' | 'Error'>>;
  phoneError: string;
  setPhoneError: (error: string) => void;

  agreeRulesPix: boolean;
  setAgreeRulesPix: (agree: boolean) => void;
}

const ContactComunixFormDataContext =
  createContext<ContactComunixFormDataContextData>(
    {} as ContactComunixFormDataContextData,
  );

export const ContactComunixFormDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [namePartner, setNamePartner] = useState<string | null>(null);
  const [namePartnerStatus, setNamePartnerStatus] = useState<'Error' | ''>('');
  const [namePartnerError, setNamePartnerError] = useState('');

  const [cnpj, setCnpj] = useState<string | null>(null);
  const [cpf, setCpf] = useState<string | null>(null);
  const [cpfCnpjStatus, setCpfCnpjStatus] = useState<'Error' | ''>('');
  const [cpfCnpjError, setCpfCnpjError] = useState('');

  const [email, setEmail] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<'Error' | ''>('');
  const [emailError, setEmailError] = useState('');

  const [phone, setPhone] = useState<string | null>(null);
  const [phoneStatus, setPhoneStatus] = useState<'Error' | ''>('');
  const [phoneError, setPhoneError] = useState('');

  const [agreeRulesPix, setAgreeRulesPix] = useState(false);

  const addSignedUserContactToComunixBatch = async (
    batchCode: number,
  ): Promise<void> => {
    try {
      const signedUserData = await getUserSigned();

      if (!signedUserData) {
        throw new Error('Invalid signer user data.');
      }

      const { name, document, email, phone } = signedUserData;

      if (!name || !document || !email || !phone) {
        throw new Error('Missing required signer user fields.');
      }

      // Initialize cpf and cnpj with default values
      let cpf = '00000000000'; // Default value for CPF
      let cnpj = '00000000000000'; // Default value for CNPJ

      // Determine if the document is CPF or CNPJ and validate
      if (isValidCPF(document)) {
        cpf = document;
      } else if (isValidCNPJ(document)) {
        cnpj = document;
      } else {
        throw new Error(
          'Document must have 11 (CPF) or 14 (CNPJ) valid characters.',
        );
      }

      const userContact: IAddUserContactToConsultsBatchRequest = {
        cpf,
        cnpj,
        name,
        email,
        phone,
      };

      await addUserContactToConsultsBatch(batchCode, userContact);

      console.info(`User contact successfully added to batch ${batchCode}.`);
    } catch (error) {
      console.warn(
        `ContactComunixFormDataProvider.addSignedUserContactToComunixBatch ~
         Failed to add user contact to batch ${batchCode}.`,
      );
      console.warn(
        'ContactComunixFormDataProvider.addSignedUserContactToComunixBatch ~ Error:',
        error instanceof Error ? error.message : error,
      );
    }
  };

  return (
    <ContactComunixFormDataContext.Provider
      value={{
        addSignedUserContactToComunixBatch,

        namePartner,
        setNamePartner,
        namePartnerStatus,
        setNamePartnerStatus,
        namePartnerError,
        setNamePartnerError,

        cnpj,
        setCnpj,
        cpf,
        setCpf,
        cpfCnpjStatus,
        setCpfCnpjStatus,
        cpfCnpjError,
        setCpfCnpjError,

        email,
        setEmail,
        emailStatus,
        setEmailStatus,
        emailError,
        setEmailError,

        phone,
        setPhone,
        phoneStatus,
        setPhoneStatus,
        phoneError,
        setPhoneError,

        agreeRulesPix,
        setAgreeRulesPix,
      }}
    >
      {children}
    </ContactComunixFormDataContext.Provider>
  );
};

export function useContactComunixFormData(): ContactComunixFormDataContextData {
  const context = useContext(ContactComunixFormDataContext);

  if (!context) {
    throw new Error(
      'usePayment must be used within an ContactComunixFormDataProvider',
    );
  }

  return context;
}
